import { OtsDateTime } from '../../components/OtsDateTime';
import { useId, type ReactNode } from 'react';
import type { RrcCompanyProfile } from '@ots-frontend/ots-data';
import { AdminPanel } from '../../components/AdminPanel';
import {
  DescriptionList,
  DescriptionItem,
} from '../../components/DescriptionList';
import { ComplianceStatusBadge } from '../ComplianceStatusBadge';
import { companyProfileCodeLabel } from './rrc-company-profile-ui';

export type RrcCompanyProfileDetailsProps = {
  profile: RrcCompanyProfile;
  compact?: boolean;
  actions?: ReactNode;
};
export const RrcCompanyProfileDetails = ({
  profile,
  compact,
  actions,
}: RrcCompanyProfileDetailsProps) => {
  const headingId = useId();
  return (
    <div className="@container space-y-4">
      <AdminPanel
        ariaLabelledBy={headingId}
        title={profile.orgName ?? profile.companyName ?? 'Company profile'}
        compact={compact}
        description={`Version ${profile.version} · ${companyProfileCodeLabel(
          profile.role
        )} · ${companyProfileCodeLabel(profile.rrcDistrict)}`}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ComplianceStatusBadge status={profile.status} compact={compact} />
            {actions}
          </div>
        }
        bodyClassName="@container p-4"
      >
        <DescriptionList compact={compact} responsive="container">
          <DescriptionItem label="Company">
            {profile.companyName ?? profile.companyId}
          </DescriptionItem>
          <DescriptionItem label="Effective start">
            <OtsDateTime value={profile.effectiveStartDttm} empty="Not set" />
          </DescriptionItem>
          <DescriptionItem label="Effective end">
            <OtsDateTime value={profile.effectiveEndDttm} empty="No end date" />
          </DescriptionItem>
          <DescriptionItem label="P5 number">
            {profile.p5Number}
          </DescriptionItem>
          <DescriptionItem label="Permit number">
            {profile.permitNumber}
          </DescriptionItem>
          <DescriptionItem label="Commercial">
            {profile.commercial ? 'Yes' : 'No'}
          </DescriptionItem>
        </DescriptionList>
      </AdminPanel>
      <AdminPanel
        title="Contact & address"
        compact={compact}
        bodyClassName="@container p-4"
      >
        <DescriptionList compact={compact} responsive="container">
          <DescriptionItem label="Contact">
            {profile.contactName}
          </DescriptionItem>
          <DescriptionItem label="Phone">
            {profile.contactPhone}
          </DescriptionItem>
          <DescriptionItem label="Email">
            {profile.contactEmail}
          </DescriptionItem>
          <DescriptionItem label="Address">
            {[
              profile.address?.street,
              profile.address?.street2,
              profile.address?.city,
              profile.address?.state,
              profile.address?.province,
              profile.address?.postalCode,
              profile.address?.country,
            ]
              .filter(Boolean)
              .join(', ')}
          </DescriptionItem>
          <DescriptionItem label="County">
            {profile.address?.county}
          </DescriptionItem>
        </DescriptionList>
      </AdminPanel>
      <AdminPanel
        title="Automatic certification"
        compact={compact}
        bodyClassName="@container p-4"
      >
        <DescriptionList compact={compact} responsive="container">
          <DescriptionItem label="EP-9 signer strategy">
            {companyProfileCodeLabel(profile.autoEp9SignerStrategy)}
          </DescriptionItem>
          <DescriptionItem label="EP-9 signer">
            {profile.autoEp9SignerName ?? profile.autoEp9SignerId}
          </DescriptionItem>
          <DescriptionItem label="EP-10 signer strategy">
            {companyProfileCodeLabel(profile.autoEp10SignerStrategy)}
          </DescriptionItem>
          <DescriptionItem label="EP-10 signer">
            {profile.autoEp10SignerName ?? profile.autoEp10SignerId}
          </DescriptionItem>
        </DescriptionList>
      </AdminPanel>
      <AdminPanel
        title="Record history"
        compact={compact}
        bodyClassName="@container p-4"
      >
        <DescriptionList compact={compact} responsive="container">
          <DescriptionItem label="Created">
            <OtsDateTime value={profile.createDttm} empty="—" />
          </DescriptionItem>
          <DescriptionItem label="Created by">
            {profile.createdByName}
          </DescriptionItem>
          <DescriptionItem label="Updated">
            <OtsDateTime value={profile.lastModifiedDttm} empty="—" />
          </DescriptionItem>
          <DescriptionItem label="Updated by">
            {profile.lastModifiedByName}
          </DescriptionItem>
        </DescriptionList>
      </AdminPanel>
    </div>
  );
};
